import axios from 'axios';
import { apiClient, IS_DEV } from './apiClient.ts';

export interface Product {
  id: string;
  product_name: string;
  short_desc?: string;
  status?: string;
  product_family?: string;
  sku?: string;
  revision?: string;
  spec?: string;
  buy_spec?: string;
  price?: string;
  buy_link?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProductDTO {
  product_name: string;
  short_desc?: string;
  status?: string;
  product_family?: string;
  sku?: string;
  revision?: string;
  spec?: string;
  buy_spec?: string;
  price?: string;
  buy_link?: string;
  image_url?: string;
}

export interface UpdateProductDTO {
  product_name?: string;
  short_desc?: string;
  status?: string;
  product_family?: string;
  sku?: string;
  revision?: string;
  spec?: string;
  buy_spec?: string;
  price?: string;
  buy_link?: string;
  image_url?: string;
}

class ProductService {
  private apiClient = apiClient;

  async getAllProducts(skip: number = 0, limit: number = 100): Promise<Product[]> {
    try {
      const response = await this.apiClient.get(`/api/products?skip=${skip}&limit=${limit}`);
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error fetching products:', error);
      throw error;
    }
  }

  async getProductById(productId: string): Promise<Product> {
    try {
      const response = await this.apiClient.get(`/api/products/${productId}`);
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error fetching product:', error);
      throw error;
    }
  }

  async createProduct(productData: CreateProductDTO): Promise<Product> {
    try {
      const response = await this.apiClient.post('/api/products', productData);
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error creating product:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.detail || 'Failed to create product');
      }
      throw new Error('Failed to create product');
    }
  }

  async updateProduct(productId: string, productData: UpdateProductDTO): Promise<Product> {
    try {
      const response = await this.apiClient.put(`/api/products/${productId}`, productData);
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error updating product:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.detail || 'Failed to update product');
      }
      throw new Error('Failed to update product');
    }
  }

  async deleteProduct(productId: string): Promise<void> {
    try {
      await this.apiClient.delete(`/api/products/${productId}`);
    } catch (error) {
      if (IS_DEV) console.error('Error deleting product:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.detail || 'Failed to delete product');
      }
      throw new Error('Failed to delete product');
    }
  }

  async uploadImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.apiClient.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.url || response.data.image_url;
    } catch (error) {
      if (IS_DEV) console.error('Error uploading image:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.detail || 'Failed to upload image');
      }
      throw new Error('Failed to upload image');
    }
  }
}

export const productService = new ProductService();
